import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid2,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import config from '../common/Config';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import Header from '../Header';
import Footer from '../Footer';
import '../common/commonStyles.css';

const ProductLedgerView = () => {
  const accessToken = localStorage.getItem('accessToken');
  const { productId, startDate, endDate, orderType } = useParams();

  const [productLedger, setProductLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [resStartDate, setResStartDate] = useState(startDate || '');
  const [resEndDate, setResEndDate] = useState(endDate || '');

  const fetchProductLedger = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('start', 0);
      formData.append('length', 10000);
      formData.append('searchValue', searchValue);
      formData.append('product_id', productId || '');
      formData.append('start_date', startDate || '');
      formData.append('end_date', endDate || '');
      formData.append('order_type', orderType || '');

      const response = await fetch(`${config.API_URL}/view_product_ledger_details`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || 'Failed to fetch');
      }

      let rows = Array.isArray(json.data) ? json.data : [];
      if (rows.length > 0 && rows[0].order_type) {
        const openingBalanceRow = {
          date: '',
          order_no: '',
          code: '',
          name: '',
          order_type: rows[0].order_type,
          status: 'O/B',
          qty: '',
          bal: rows[0].order_type === 'box'
            ? (rows[0].box_system_qty ?? rows[0].actual_box_balance ?? '')
            : (rows[0].piece_system_qty ?? rows[0].actual_piece_balance ?? ''),
        };
        rows = [openingBalanceRow, ...rows];
      }
      setProductLedger(rows);
      if (json.start_date) setResStartDate(json.start_date);
      if (json.end_date) setResEndDate(json.end_date);
    } catch (err) {
      toast.error('Failed to load product movement');
      setProductLedger([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, productId, startDate, endDate, orderType, searchValue]);

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchProductLedger();
  }, [accessToken, fetchProductLedger]);

  const getBalanceDisplay = (row) => {
    if (row.order_type === 'box') return row.actual_box_balance ?? row.bal ?? '';
    if (row.order_type === 'piece') return row.actual_piece_balance ?? row.bal ?? '';
    return row.bal ?? '';
  };

  const getOrderRefText = (row) =>
    row.order_no ||
    row.orderNo ||
    row.grn_no ||
    row.grnNo ||
    row.invoice_no ||
    '';

  const getOrderNoLink = (row) => {
    const text = getOrderRefText(row);
    if (!text) return '';

    const saleRaw = row.sale_id ?? row.saleId;
    const saleId = saleRaw != null && String(saleRaw).trim() !== '' ? String(saleRaw).trim() : '';
    const hasValidSale = saleId !== '' && saleId !== '0';

    const st = String(row.status ?? '').trim();
    const isGrn = st === 'GRN' || st.toLowerCase() === 'grn';

    if (isGrn) {
      const gid = row.grn_id ?? row.grnId;
      if (gid != null && String(gid).trim() !== '') {
        const url = `${config.BASE_URL}/purchases/view_grn/${encodeURIComponent(String(gid).trim())}`;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#212b36', textDecoration: 'underline' }}>
            {text}
          </a>
        );
      }
      return text;
    }

    if (!hasValidSale) {
      return text;
    }

    const url = st === 'Accept'
      ? `${config.BASE_URL}/new_sales/invoice/${saleId}`
      : `${config.BASE_URL}/trip_summary/print/${saleId}`;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#212b36', textDecoration: 'underline' }}>
        {text}
      </a>
    );
  };

  const formatCustomerCell = (row) => {
    const nameRaw = row.party_name ?? row.customer_name ?? '';
    const acctRaw = row.party_account_no ?? row.customer_account_no ?? '';
    let name = String(nameRaw).trim();
    let acct = String(acctRaw).trim();
    if (name === 'N/A') name = '';
    if (acct === 'N/A' || acct === '') acct = '';
    if (name && acct) return `${name}(${acct})`;
    return name || acct;
  };

  const handleExport = async () => {
    try {
      const response = await axios.post(
        `${config.API_URL}/view_product_ledger_details_export`,
        {
          searchValue: searchValue || '',
          product_id: productId || '',
          start_date: startDate || '',
          end_date: endDate || '',
          order_type: orderType || '',
        },
        { headers: { authorization: `Bearer ${accessToken}` } }
      );

      if (!response.data?.data || !Array.isArray(response.data.data)) {
        toast.error('No data to export');
        return;
      }

      const exportData = response.data.data.map((row) => ({
        Date: row.date || '',
        'Order/Inv No.': row.order_no || row.grn_no || row.invoice_no || '',
        'SUPP / CUST Name': formatCustomerCell(row),
        Type: row.order_type || '',
        Transaction: row.status || '',
        Qty: row.qty != null && row.qty !== '' ? parseFloat(row.qty) : '',
        Balance:
          row.order_type === 'box'
            ? (row.actual_box_balance != null && row.actual_box_balance !== '' ? parseFloat(row.actual_box_balance) : '')
            : row.order_type === 'piece'
              ? (row.actual_piece_balance != null && row.actual_piece_balance !== '' ? parseFloat(row.actual_piece_balance) : '')
              : (row.bal != null && row.bal !== '' ? parseFloat(row.bal) : ''),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Product Movement');
      XLSX.writeFile(wb, `Product_Movement_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Export completed');
    } catch (err) {
      toast.error('Failed to export');
    }
  };

  const ledgerProductName = useMemo(() => {
    const row = productLedger.find((r) => r.name != null && String(r.name).trim() !== '');
    if (!row) return '';
    const name = String(row.name).trim();
    const code = String(row.code ?? '').trim();
    return code ? `${name}(${code})` : name;
  }, [productLedger]);

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={2} justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography component="span" className="page-title">
                    Product Movement View : 
                 
                   {ledgerProductName ? (
                    <Typography component="span" variant="body2" sx={{ color: '#212b36', fontWeight: 500 }}>
                       {ledgerProductName}
                    </Typography>
                  ) : null}
                   </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                <TextField
                  size="small"
				  className="input-border"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchProductLedger()}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: '#637381' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchValue('')} edge="end">
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    width: '100%',
                    maxWidth: 280,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f7fa',
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <Button className="button-variant-contained" variant="contained" size="small" onClick={handleExport} disabled={loading || productLedger.length === 0}>
                  Export
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <Box sx={{ px: 2, pb: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: '70vh',
                  overflow: 'auto',
                  border: '1px solid rgba(145, 158, 171, 0.16)',
                  borderRadius: 1,
                }}
              >
                <Table size="medium" className="table-cell-border" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '12%' }}>Date</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '14%' }}>Order/Inv No.</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '22%' }}>SUPP / CUST Name</TableCell>
                      {/* <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '12%' }}>Type</TableCell> */}
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '10%' }}>Type</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '12%' }}>Transaction</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '8%', textAlign: 'right' }}>Qty</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: '10%', textAlign: 'right' }}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productLedger.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
                          No ledger data
                        </TableCell>
                      </TableRow>
                    )}
                    {productLedger.map((row, index) => (
                      <TableRow
                        key={`${row.date}-${row.order_no ?? ''}-${row.grn_no ?? ''}-${row.id ?? ''}-${index}`}
                        sx={{
                          '& .MuiTableCell-root': { fontSize: '0.75rem', color: '#212b36', py: 1 },
                        }}
                      >
                        <TableCell>{row.date}</TableCell>
                        <TableCell sx={{ textDecoration: 'underline !important',}}>{getOrderNoLink(row)}</TableCell>
                        <TableCell>{formatCustomerCell(row)}</TableCell>
                        {/* <TableCell>{row.code}</TableCell> */}
                        <TableCell>{row.order_type}</TableCell> 
                        <TableCell>{row.status}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{row.qty}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{getBalanceDisplay(row)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ProductLedgerView;
